import React, { useMemo } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

interface PhaseNodeState {
  isCompleted: boolean;
  isCurrent: boolean;
}

function getPhaseNodeClasses(phase: PhaseNodeState, isDark: boolean): { circleClasses: string; nameClasses: string } {
  let circleClasses: string;
  if (phase.isCompleted) circleClasses = 'bg-primary text-white';
  else if (phase.isCurrent) circleClasses = 'bg-primary/20 text-primary border-2 border-primary';
  else if (isDark) circleClasses = 'bg-white/5 text-white/30 border border-white/10';
  else circleClasses = 'bg-slate-100 text-slate-400 border border-slate-200';

  let nameClasses: string;
  if (phase.isCurrent) nameClasses = 'text-primary';
  else if (phase.isCompleted) nameClasses = isDark ? 'text-white/60' : 'text-slate-600';
  else nameClasses = isDark ? 'text-white/25' : 'text-slate-400';

  return { circleClasses, nameClasses };
}

function getConnectorClasses(i: number, currentPhaseIndex: number, isDark: boolean): string {
  if (i < currentPhaseIndex) return 'bg-primary';
  return isDark ? 'bg-white/10' : 'bg-slate-200';
}

const SessionJourneyMapWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const currentPhaseIndex = state?.currentPhaseIndex ?? 0;

  // Compute stats per phase
  const phaseStats = useMemo(() => {
    const phases = state?.phases ?? [];
    const pendingContributions = state?.pendingContributions ?? [];
    return phases.map((phase, i) => {
      // Count contributions accepted in this phase (approximate)
      const accepted = pendingContributions.filter(c => c.state === 'accepted').length;
      return {
        ...phase,
        isCompleted: i < currentPhaseIndex,
        isCurrent: i === currentPhaseIndex,
        isFuture: i > currentPhaseIndex,
        contributions: i === currentPhaseIndex ? accepted : 0,
      };
    });
  }, [state?.phases, currentPhaseIndex, state?.pendingContributions]);

  if (!state || phaseStats.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No phases configured
      </div>
    );
  }

  return (
    <div className="flex items-center h-full px-4 gap-1 overflow-x-auto">
      {phaseStats.map((phase, i) => {
        const { circleClasses, nameClasses } = getPhaseNodeClasses(phase, isDark);
        const connectorClasses = getConnectorClasses(i, currentPhaseIndex, isDark);

        return (
          <React.Fragment key={phase.id}>
            {/* Phase node */}
            <div className="flex flex-col items-center gap-1.5 shrink-0 min-w-[80px]">
              {/* Circle */}
              <div className="relative">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 ${circleClasses}`}
                >
                  {phase.isCompleted ? '✓' : i + 1}
                </div>
                {/* Pulsing ring for current phase */}
                {phase.isCurrent && (
                  <div className="absolute inset-0 -m-1 rounded-full border-2 border-primary/40 animate-ping" />
                )}
              </div>

              {/* Phase name */}
              <span className={`text-xs font-semibold text-center leading-tight max-w-[80px] ${nameClasses}`}>
                {phase.name}
              </span>

              {/* Duration */}
              {phase.duration && (
                <span className={`text-xs ${isDark ? 'text-white/15' : 'text-slate-300'}`}>
                  {phase.duration}
                </span>
              )}
            </div>

            {/* Connector line */}
            {i < phaseStats.length - 1 && (
              <div className="flex-1 min-w-[16px] h-px relative">
                <div className={`absolute inset-0 ${connectorClasses}`} />
                {/* Animated dot on the current connector */}
                {i === currentPhaseIndex - 1 && (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                )}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default SessionJourneyMapWidget;
